import { useEffect, useState } from 'react';
import { Auth } from 'aws-amplify';
import { useNavigate } from 'react-router-dom';

import * as MESSAGES from '../../../Lib/Messages';
import Notification from '../../UI_Library/Notification/Notification';
import Validator from '../../../Lib/Validator';
import LoadingBar from 'Components/UI_Library/LoadingIndicators/LoadingBar';
import Button from 'Components/UI_Library/FormElements/Button';
import { mfaAPI } from 'Backend/users/mfaAPI';
import { useGetUser } from 'Backend/users/getUser';
import classes from './MFA.module.css';
import SMSMFA from './Components/SMSMFA';
import TOTPMFA from './Components/TOTPMFA';
import Toggle from 'Components/UI_Library/FormElements/Toggle';

const MFA = ({ setRefreshMFA }: { setRefreshMFA?: (refresh: boolean) => void }) => {
    const [code, setCode] = useState<string>();
    const [token, setToken] = useState<string>();
    const [TOTPCode, setTOTPCode] = useState<string>();
    const [phoneNumber, setPhoneNumber] = useState<string>();

    const [loading, setLoading] = useState<boolean>(true);
    const [loadingSMS, setLoadingSMS] = useState<boolean>(false);
    const [loadingAuthApp, setLoadingAuthApp] = useState<boolean>(false);
    const [errors, setErrors] = useState<{ code?: string; TOTPCode?: string }>({});
    const [isError, setIsError] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>();

    const { data: user } = useGetUser();
    const [isFirstSend, setIsFirstSend] = useState<boolean>(false);
    const [isSMSForm, setIsSMSForm] = useState<boolean>(false);
    const [isTOTPForm, setIsTOTPForm] = useState<boolean>(false);
    const [isVerifyPhone, setIsVerifyPhone] = useState<boolean>(false);
    const [authPhoneNumber, setAuthPhoneNumber] = useState<string>();
    const [QRToken, setQRToken] = useState<string>();
    const [listMFA, setListMFA] = useState<('SMS' | 'TOTP')[]>();
    const [disable, setDisable] = useState<'SMS' | 'TOTP'>();
    const [copyButtonText, setCopyButtonText] = useState<string>('Copy code');
    const [codeAsText, setCodeAsText] = useState<boolean>(false);
    const [userAuthSettings, setUserAuthSettings] = useState<object>();

    const history = useNavigate();

    const initialLoad = async () => {
        try {
            const user = await Auth.currentAuthenticatedUser();
            if (!user) {
                history('/signout');
            }

            const {
                signInUserSession: {
                    accessToken: { jwtToken },
                },
            } = user;

            const res = await mfaAPI.getMFAsettings({ token: jwtToken });

            setUserAuthSettings(user);
            setIsVerifyPhone(user.attributes.phone_number_verified || false);
            setAuthPhoneNumber(user.attributes.phone_number);
            setListMFA(res?.data?.enabledMfaOptions || []);
        } catch (err) {
            console.log('err', err);
        }
        setLoading(false);
    };
    useEffect(() => {
        initialLoad();
    }, []);

    useEffect(() => {
        if (user?.mobile && !phoneNumber) setPhoneNumber(user.mobile);
    }, [user]);

    const handleSendSMSCode = async () => {
        try {
            setLoadingSMS(true);

            await Auth.updateUserAttributes(userAuthSettings, {
                phone_number: phoneNumber,
            });
            await Auth.verifyUserAttribute(userAuthSettings, 'phone_number');

            setIsFirstSend(true);
            setErrorMessage(MESSAGES.SUCCESS_SEND_CODE_MFA_SMS);
            setIsError(false);
        } catch {
            setErrorMessage(MESSAGES.ERROR_SEND_CODE_MFA_SMS);
            setIsError(true);
        }
        setLoadingSMS(false);
    };

    const handleDisableSMS = () => {
        setDisable('SMS');
    };

    const getJWTToken = async () => {
        const user = await Auth.currentAuthenticatedUser();
        if (!user) {
            history('/signout');
        }
        const {
            signInUserSession: {
                accessToken: { jwtToken },
            },
        } = user;
        return jwtToken;
    };

    const addUserMFA = async (method: 'SMS' | 'TOTP') => {
        const isSMS = method === 'SMS';
        if (isSMS) {
            setLoadingSMS(true);
        } else {
            setLoadingAuthApp(true);
        }

        const updateMFASettings = await mfaAPI.updateMFASettings({
            token: await getJWTToken(),
            preferredMfaMethod: method,
        });
        if (updateMFASettings.success) {
            if (!listMFA?.includes(method)) {
                listMFA?.push(method);
            }
            setListMFA(listMFA);
            setErrorMessage(isSMS ? MESSAGES.SUCCESS_ENABLE_MFA_SMS : MESSAGES.SUCCESS_ENABLE_MFA_TOTP);
            setIsError(false);

            if (isSMS) {
                setIsSMSForm(false);
            } else {
                setIsTOTPForm(false);
            }
            if (setRefreshMFA) {
                setRefreshMFA(true);
            }
        } else {
            setErrorMessage(isSMS ? MESSAGES.ERROR_ENABLE_MFA_SMS : MESSAGES.ERROR_ENABLE_MFA_TOTP);
            setIsError(true);
        }

        setLoadingSMS(false);
        setLoadingAuthApp(false);
    };

    const removeUserMFA = async (method: 'SMS' | 'TOTP') => {
        const isSMS = method === 'SMS';
        if (isSMS) {
            setLoadingSMS(true);
        } else {
            setLoadingAuthApp(true);
        }

        const filteredList = listMFA?.filter((value) => value !== method) || [];

        const updateMFASettings = await mfaAPI.updateMFASettings({
            token: await getJWTToken(),
            preferredMfaMethod: filteredList?.[0] || undefined,
        });
        if (updateMFASettings.success) {
            setListMFA(filteredList);
            setErrorMessage(isSMS ? MESSAGES.SUCCESS_DISABLE_MFA_SMS : MESSAGES.SUCCESS_DISABLE_MFA_TOTP);
            setIsError(false);
            if (setRefreshMFA) {
                setRefreshMFA(true);
            }
        } else {
            setErrorMessage(isSMS ? MESSAGES.ERROR_DISABLE_MFA_SMS : MESSAGES.ERROR_DISABLE_MFA_TOTP);
            setIsError(true);
        }

        setLoadingSMS(false);
        setLoadingAuthApp(false);
        setDisable(undefined);
    };

    const handleEnableSMS = async () => {
        if (!isVerifyPhone || authPhoneNumber !== phoneNumber) {
            setIsSMSForm(true);
            setIsFirstSend(false);
        } else {
            addUserMFA('SMS');
        }
    };

    const handleVerifySMSCode = async () => {
        if (!isValidation()) {
            return false;
        }

        setLoadingSMS(true);

        try {
            await Auth.verifyCurrentUserAttributeSubmit('phone_number', code || '');
            addUserMFA('SMS');
        } catch {
            setErrorMessage(MESSAGES.ERROR_INVALID_CODE_MFA_SMS);
            setIsError(true);
        }

        setLoadingSMS(false);
    };

    const handleDisableTOTP = () => {
        setDisable('TOTP');
    };

    const handleEnableTOTP = async () => {
        try {
            const code = await Auth.setupTOTP(userAuthSettings);
            const str = `otpauth://totp/Paytron (${user?.email})?secret=${code}`;
            setQRToken(str);
            setToken(code);
            setIsTOTPForm(true);
        } catch (err) {
            console.log('err', err);
        }
    };

    const handleVerifyTOTPCode = async () => {
        if (!isValidation('TOTP')) {
            return false;
        }
        setLoadingAuthApp(true);

        try {
            await Auth.verifyTotpToken(userAuthSettings, TOTPCode || '');
            await addUserMFA('TOTP');
        } catch {
            setErrorMessage(MESSAGES.ERROR_INVALID_CODE_MFA_TOTP);
            setIsError(true);
        }
        setLoadingAuthApp(false);
    };

    const isValidation = (method?: 'SMS' | 'TOTP') => {
        setErrors({});
        setErrorMessage('');

        let isError = false;
        const validator = new Validator();

        if (method === 'TOTP') {
            if (!validator.required(TOTPCode)) {
                setErrors({ TOTPCode: MESSAGES.ERROR_REQUIRED });
                isError = true;
            } else if (TOTPCode?.length !== 6) {
                setErrors({ TOTPCode: MESSAGES.ERROR_NUMBER });
                isError = true;
            }
        } else if (!validator.required(code)) {
            setErrors({ code: MESSAGES.ERROR_REQUIRED });
            isError = true;
        } else if (code?.length !== 6) {
            setErrors({ code: MESSAGES.ERROR_NUMBER });
            isError = true;
        }
        if (isError) {
            return false;
        }

        return true;
    };

    const copyToClipboard = () => {
        navigator.clipboard.writeText(token || '');
        setCopyButtonText('Copied code');
        setTimeout(() => {
            setCopyButtonText('Copy code');
        }, 1500);
    };

    const toggleTextCode = () => {
        setCodeAsText(!codeAsText);
    };
    const exitForm = () => {
        setIsTOTPForm(false);
        setIsSMSForm(false);
    };

    useEffect(() => {
        setErrorMessage(undefined);
    }, [disable]);

    return (
        <>
            {loading ? (
                <div style={{ height: '380px' }}>
                    <LoadingBar />
                </div>
            ) : (
                <div className={classes.mfa}>
                    <div className={classes.container}>
                        <div className={classes.form}>
                            <div className={classes.options}>
                                <div className={classes.method}>
                                    <div className={classes.top}>
                                        <div
                                            className={classes.toggle}
                                            data-testid="SMS-test"
                                            onClick={
                                                listMFA?.includes('SMS')
                                                    ? () => handleDisableSMS()
                                                    : () => handleEnableSMS()
                                            }
                                        >
                                            <Toggle size="L" toggled={listMFA?.includes('SMS')} disabled={loadingSMS} />
                                            <h5>SMS</h5>
                                        </div>
                                    </div>
                                    {isSMSForm ? (
                                        <SMSMFA
                                            phoneNumber={phoneNumber || ''}
                                            code={code || ''}
                                            loadingSMS={loadingSMS}
                                            isFirstSend={isFirstSend}
                                            errors={errors}
                                            exitForm={exitForm}
                                            handleVerifySMSCode={handleVerifySMSCode}
                                            handleSendSMSCode={handleSendSMSCode}
                                            setCode={setCode}
                                        />
                                    ) : null}
                                </div>
                                <div className={classes.method}>
                                    <div className={classes.top}>
                                        <div
                                            className={classes.toggle}
                                            data-testid="TOTP-test"
                                            onClick={
                                                listMFA?.includes('TOTP')
                                                    ? () => handleDisableTOTP()
                                                    : () => handleEnableTOTP()
                                            }
                                        >
                                            <Toggle
                                                size="L"
                                                toggled={listMFA?.includes('TOTP')}
                                                disabled={loadingAuthApp}
                                            />
                                            <h5>Authentication app</h5>
                                        </div>
                                    </div>
                                    {isTOTPForm ? (
                                        <TOTPMFA
                                            exitForm={exitForm}
                                            copyToClipboard={copyToClipboard}
                                            toggleTextCode={toggleTextCode}
                                            handleVerifyTOTPCode={handleVerifyTOTPCode}
                                            setTOTPCode={setTOTPCode}
                                            TOTPCode={TOTPCode}
                                            QRToken={QRToken}
                                            loadingAuthApp={loadingAuthApp}
                                            codeAsText={codeAsText}
                                            copyButtonText={copyButtonText}
                                            errors={errors}
                                        />
                                    ) : null}
                                </div>
                            </div>

                            {['SMS', 'TOTP'].includes(disable || '') ? (
                                <Notification
                                    message="Are you sure you want to disable MFA?"
                                    type="warning"
                                    style={{ flex: 1, width: '100%', padding: '0 0 0 5px' }}
                                    buttons={
                                        <Button
                                            type="white-condensed"
                                            text="Yes, disable it"
                                            onClick={() => {
                                                if (disable === 'SMS') {
                                                    removeUserMFA('SMS');
                                                } else if (disable === 'TOTP') {
                                                    removeUserMFA('TOTP');
                                                }
                                                setDisable(undefined);
                                            }}
                                        />
                                    }
                                />
                            ) : null}

                            <Notification
                                style={{ padding: 0, width: '100%' }}
                                isError={isError}
                                message={errorMessage}
                                handleCloseMsg={() => setErrorMessage('')}
                            />
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default MFA;
